import AddIcon from "@mui/icons-material/Add";
import { Box, MenuItem, Stack, TextField } from "@mui/material";
import { Button, Typography } from "@platform/design-system";
import { forwardRef, useImperativeHandle, useRef, useState } from "react";

import {
  CustomTextEditorFields,
  CustomTextModeToggle,
} from "./CustomTextEditorFields";
import {
  type AgeReductionScheduleEntry,
  type DraftEntry,
  isValidEntry,
  makeDraftEntry,
  ScheduleBandsField,
  toDraftEntries,
} from "./ScheduleBandsField";
import { writeCollapsedSiblings, writeSibling } from "./siblingWrites";
import { useCustomTextEditor } from "./useCustomTextEditor";
import { usePopupEditorKeyboard } from "./usePopupEditorKeyboard";

import type { ICellEditorParams } from "ag-grid-community";
import type { JsonValue } from "../utils/popupCustomText";

export type { AgeReductionScheduleEntry } from "./ScheduleBandsField";

export interface AgeReductionTypeOption {
  value: string;
  label: string;
}

export interface AgeReductionScheduleCellEditorParams {
  initialType: string | undefined;
  initialSchedules: AgeReductionScheduleEntry[];
  typeKey: string;
  detailKey: string;
  typeOptions: AgeReductionTypeOption[];
  /** The type value that requires a schedule (e.g. "percentageAtAge"). */
  schedulesForType: string;
  initialCustomText?: string | null;
  onCustomTextChange?: (
    customText: string | null,
    originalValue?: JsonValue,
  ) => void;
}

type Props = ICellEditorParams & AgeReductionScheduleCellEditorParams;

// Fixed size so adding bands never resizes the popover (AG Grid would otherwise
// reposition it). Content scrolls; the whole thing opens lifted up so it clears
// the bottom of the viewport.
const POPOVER_WIDTH = 440;

/**
 * AG Grid popup cell editor for Age Reduction Schedules, shared across every LOC
 * that exposes one (Critical Illness, Basic Life, Voluntary Life, Accident).
 *
 * Renders a type dropdown and, when the schedule-bearing type is selected, a
 * list of `reduce-to X% at age Y` bands. On save it writes the schedule array
 * back to the collapsed `*Schedules` sibling and the type to the `*Type`
 * sibling.
 */
export const AgeReductionScheduleCellEditor = forwardRef<unknown, Props>(
  function AgeReductionScheduleCellEditor(props, ref) {
    const {
      initialType,
      initialSchedules,
      typeKey,
      detailKey,
      typeOptions,
      schedulesForType,
      initialCustomText,
      onCustomTextChange,
      api,
      colDef,
      column,
      node,
      stopEditing,
    } = props;

    const [draftType, setDraftType] = useState<string>(initialType ?? "");

    const [entries, setEntries] = useState<DraftEntry[]>(
      toDraftEntries(initialSchedules),
    );

    const [showErrors, setShowErrors] = useState(false);

    const customText = useCustomTextEditor(
      initialCustomText,
      onCustomTextChange,
      stopEditing,
    );
    const { editingCustomText } = customText;

    const lastValueRef = useRef<unknown>(initialType ?? null);

    useImperativeHandle(ref, () => ({
      getValue: () => lastValueRef.current,
      isPopup: () => true,
      isCancelBeforeStart: () => false,
      isCancelAfterEnd: () => false,
    }));

    const showSchedule = draftType === schedulesForType;

    const updateEntry = (
      index: number,
      field: keyof DraftEntry,
      value: string,
    ): void => {
      setEntries((prev) =>
        prev.map((entry, i) =>
          i === index ? { ...entry, [field]: value } : entry,
        ),
      );
    };

    const addEntry = (): void => {
      setEntries((prev) => [...prev, makeDraftEntry()]);
    };

    const removeEntry = (index: number): void => {
      setEntries((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSave = (): void => {
      if (editingCustomText) {
        lastValueRef.current = initialType ?? null;
        customText.saveCustomText(initialType);

        return;
      }

      let schedules: AgeReductionScheduleEntry[] = [];

      if (showSchedule) {
        if (!entries.every(isValidEntry)) {
          setShowErrors(true);

          return;
        }

        schedules = entries.map((entry) => ({
          reductionPercent: Number(entry.reductionPercent),
          age: Number(entry.age),
        }));
      }

      lastValueRef.current = draftType || null;

      const columnField = colDef?.field;
      if (initialCustomText) onCustomTextChange?.(null);

      if (columnField && node?.data) {
        writeCollapsedSiblings(
          node as { data: Record<string, unknown> },
          columnField,
          [
            [typeKey, draftType || null],
            [detailKey, schedules],
          ],
        );
      }

      stopEditing(true);

      setTimeout(() => {
        writeSibling({ api, column, colDef }, detailKey, schedules, {
          silent: true,
        });
        writeSibling({ api, column, colDef }, typeKey, draftType || null);
        if (node) api.refreshCells({ rowNodes: [node], force: true });
      }, 0);
    };

    const handleCancel = (): void => {
      lastValueRef.current = initialType ?? null;
      stopEditing(true);
    };

    const containerRef = useRef<HTMLDivElement | null>(null);
    usePopupEditorKeyboard(containerRef, handleSave, handleCancel);

    return (
      <Box
        ref={containerRef}
        sx={{
          p: 2,
          width: POPOVER_WIDTH,
          display: "flex",
          flexDirection: "column",
          backgroundColor: "background.paper",
          boxShadow: 3,
          borderRadius: 1,
        }}
      >
        <Stack
          spacing={1.5}
          sx={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto" }}
        >
          <Typography variant="subtitle1">
            <strong>Edit Benefit Value</strong>
          </Typography>

          {customText.customTextSupported && (
            <CustomTextModeToggle
              editMode={customText.editMode}
              onChange={customText.setEditMode}
            />
          )}

          {!editingCustomText && (
            <TextField
              select
              size="small"
              label="Type"
              value={draftType}
              onChange={(e) => setDraftType(e.target.value)}
              fullWidth
            >
              {typeOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          )}

          {!editingCustomText && showSchedule && (
            <ScheduleBandsField
              entries={entries}
              showErrors={showErrors}
              onUpdate={updateEntry}
              onRemove={removeEntry}
            />
          )}

          {editingCustomText && (
            <CustomTextEditorFields
              content={customText.draftCustomText}
              onUpdate={customText.setDraftCustomText}
              onCharacterCount={customText.setDraftCharacterCount}
            />
          )}
        </Stack>

        {!editingCustomText && showSchedule && (
          <Button
            size="sm"
            variant="tertiary"
            startIcon={<AddIcon />}
            onClick={addEntry}
            sx={{ alignSelf: "flex-start", flexShrink: 0, mt: 1 }}
          >
            Add band
          </Button>
        )}

        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
          sx={{ pt: 1.5, flexShrink: 0 }}
        >
          <Button size="sm" variant="secondary" onClick={handleCancel}>
            Cancel
          </Button>

          <Button size="sm" variant="primary" onClick={handleSave}>
            Save
          </Button>
        </Stack>
      </Box>
    );
  },
);
